import { useState, useEffect } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SortableHeader } from "@/lib/tableSort";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Download, Eye, Mail, FileSpreadsheet, Edit, Trash2, Search, X, Calendar, ChevronDown, Upload, MoreHorizontal, Lock, FileText, Receipt, MessageSquare } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import InvoiceUploadModal from "@/components/InvoiceUploadModal";
import SellerMultiSelect from "@/components/SellerMultiSelect";
import MultiSelectFilter from "@/components/MultiSelectFilter";
import { toast } from "sonner";
import EmailModal from "@/components/EmailModal";
import AdvancedExportModal from "@/components/AdvancedExportModal";
import BulkUploadQuotationsModal from "@/components/BulkUploadQuotationsModal";
import OnlineInvoiceUploadModal from "@/components/OnlineInvoiceUploadModal";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function QuotationsLog() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [advancedExportOpen, setAdvancedExportOpen] = useState(false);
  const [invoiceUploadQuotation, setInvoiceUploadQuotation] = useState(null);

  // A quotation has an uploaded invoice (visual "Locked" badge). Edit-permission is separate.
  const hasInvoice = (q) => !!(q?.invoice_file_url || q?.invoice_number);
  // A quotation is "locked" for editing once its invoice has been uploaded.
  // Admins always bypass the lock so they can fix data-entry mistakes.
  const isLocked = (q) => userRole !== "admin" && hasInvoice(q);

  const handleUploadInvoiceClick = (quotation) => {
    setInvoiceUploadQuotation(quotation);
  };

  const handleDownloadInvoice = async (quotation) => {
    if (!quotation.invoice_file_url) return;
    try {
      const resp = await axios.get(`${API}/files/${quotation.invoice_file_url}`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `invoice_${quotation.invoice_number || quotation.quotation_id}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download invoice"); }
  };

  const handleDownloadEwayBill = async (quotation) => {
    if (!quotation.eway_bill_file_url) return;
    try {
      const resp = await axios.get(`${API}/files/${quotation.eway_bill_file_url}`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `eway_bill_${quotation.invoice_number || quotation.quotation_id}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download E-Way Bill"); }
  };
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [onlineInvoiceOpen, setOnlineInvoiceOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [userRole, setUserRole] = useState(null);
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [discountStatusFilter, setDiscountStatusFilter] = useState([]); // multi-select: ['valid','over_discount']
  const [statusFilter, setStatusFilter] = useState([]);                  // multi-select: ['draft','mail_sent','processing','invoice_uploaded']
  const [typeFilter, setTypeFilter] = useState([]);                       // multi-select: ['online','offline']
  const [sellerFilter, setSellerFilter] = useState([]); // array of selected seller_ids; empty = All Sellers

  // Server-side pagination + sort state (data now comes pre-filtered/sorted/paged from the backend)
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sort, setSort] = useState({ key: "order_date", order: "desc" });
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    fetchSellers();
    // Get user role from localStorage
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setUserRole(user.role);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the search box so we don't hit the server on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Any filter/sort change returns to page 1.
  useEffect(() => {
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedDateFrom, appliedDateTo, discountStatusFilter, statusFilter, sellerFilter, typeFilter, sort]);

  // Fetch the current page whenever paging/filters/sort/search change.
  useEffect(() => {
    fetchQuotations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, appliedDateFrom, appliedDateTo, discountStatusFilter, statusFilter, sellerFilter, typeFilter, sort]);

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setSellers(response.data);
    } catch (error) {
      // Error fetching sellers - silently fail
    }
  };

  // Data is already filtered, sorted and paged by the backend. Keep these aliases so the rest of
  // the (unchanged) render/bulk-select logic continues to work against the current page.
  const filteredQuotations = quotations;
  const sortedQuotations = quotations;

  // Server-driven sort: 3-state cycle (asc → desc → back to default order_date desc).
  const toggleSort = (key) => {
    setSort((cur) => {
      if (cur.key !== key) return { key, order: "asc" };
      if (cur.order === "asc") return { key, order: "desc" };
      return { key: "order_date", order: "desc" };
    });
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setDiscountStatusFilter([]);
    setStatusFilter([]);
    setSellerFilter([]);
    setTypeFilter([]);
  };

  const clearDateRangeFilter = () => {
    setDateFrom("");
    setDateTo("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setDateRangeOpen(false);
  };

  const applyDateRangeFilter = () => {
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setDateRangeOpen(false);
  };

  const hasActiveFilters = searchQuery || appliedDateFrom || appliedDateTo || discountStatusFilter.length > 0 || statusFilter.length > 0 || sellerFilter.length > 0 || typeFilter.length > 0;
  const hasActiveDateRange = appliedDateFrom || appliedDateTo;

  // Format date for display
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Get date range display text
  const getDateRangeDisplayText = () => {
    if (appliedDateFrom && appliedDateTo) {
      return `${formatDateForDisplay(appliedDateFrom)} – ${formatDateForDisplay(appliedDateTo)}`;
    } else if (appliedDateFrom) {
      return `From ${formatDateForDisplay(appliedDateFrom)}`;
    } else if (appliedDateTo) {
      return `Until ${formatDateForDisplay(appliedDateTo)}`;
    }
    return "Date Range";
  };

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      if (appliedDateFrom) params.date_from = appliedDateFrom;
      if (appliedDateTo) params.date_to = appliedDateTo;
      if (sellerFilter.length) params.seller_ids = sellerFilter.join(",");
      if (statusFilter.length) params.statuses = statusFilter.join(",");
      if (typeFilter.length) params.types = typeFilter.join(",");
      if (discountStatusFilter.length) params.validations = discountStatusFilter.join(",");
      if (sort.key && sort.order) { params.sort_by = sort.key; params.sort_order = sort.order; }
      const response = await axios.get(`${API}/quotations/log`, { params, withCredentials: true });
      setQuotations(response.data.items || []);
      setTotalCount(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
      setSelectedIds([]);
    } catch (error) {
      toast.error("Error fetching quotations");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredQuotations.map(q => q.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      await Promise.all(
        selectedIds.map(id => 
          axios.delete(`${API}/quotations/${id}`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          })
        )
      );
      toast.success(`${selectedIds.length} quotation(s) deleted successfully`);
      fetchQuotations();
    } catch (error) {
      toast.error("Error deleting quotations");
    }
  };

  const handleDownloadQuotation = async (quotation, e) => {
    e.stopPropagation();
    try {
      const response = await axios.get(`${API}/quotations/${quotation.id}/download`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quotation_${quotation.quotation_id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Quotation downloaded successfully");
    } catch (error) {
      toast.error("Error downloading quotation");
    }
  };

  const handleEmailClick = async (quotation, e) => {
    e.stopPropagation();
    setSelectedQuotation(quotation);
    
    // Fetch seller details for email subject
    try {
      const sellerResponse = await axios.get(`${API}/sellers/${quotation.seller_id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setSelectedSeller(sellerResponse.data);
    } catch (sellerError) {
      console.error("Error fetching seller:", sellerError);
      setSelectedSeller(null);
    }
    
    setEmailModalOpen(true);
  };

  const handleDeleteClick = (quotation, e) => {
    e.stopPropagation();
    setQuotationToDelete(quotation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!quotationToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/quotations/${quotationToDelete.id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      toast.success("Quotation deleted successfully");
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error deleting quotation");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setQuotationToDelete(null);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/quotations/export/excel`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'quotations.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Quotations exported successfully");
    } catch (error) {
      toast.error("Error exporting quotations");
    }
  };

  const STATUS_OPTIONS = [
    { value: "draft", label: "Draft", bg: "bg-slate-100", text: "text-slate-800" },
    { value: "mail_sent", label: "Mail Sent", bg: "bg-blue-100", text: "text-blue-800" },
    { value: "processing", label: "Processing", bg: "bg-amber-100", text: "text-amber-800" },
    { value: "ready_for_upload", label: "Ready for Upload", bg: "bg-teal-100", text: "text-teal-800" },
    { value: "validation_failed", label: "Validation Failed", bg: "bg-red-100", text: "text-red-800" },
    { value: "invoice_uploaded", label: "Invoice Uploaded", bg: "bg-success/10", text: "text-success" },
  ];

  const statusConfig = (status) => STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];

  const handleStatusChange = async (quotationId, newStatus) => {
    // Optimistic UI — patch local state first, roll back on API failure.
    const prev = quotations.find(q => q.id === quotationId)?.status;
    setQuotations(prevList => prevList.map(q => q.id === quotationId ? { ...q, status: newStatus } : q));
    try {
      await axios.patch(`${API}/quotations/${quotationId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${statusConfig(newStatus).label}`);
    } catch (err) {
      setQuotations(prevList => prevList.map(q => q.id === quotationId ? { ...q, status: prev } : q));
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const StatusCell = ({ quotation }) => {
    const cfg = statusConfig(quotation.status);
    return (
      <Select value={quotation.status} onValueChange={(v) => handleStatusChange(quotation.id, v)}>
        <SelectTrigger
          className={`h-auto py-1 px-2 rounded-full text-xs font-medium border-0 focus:ring-0 w-fit gap-1 ${cfg.bg} ${cfg.text}`}
          data-testid={`status-select-${quotation.id}`}
        >
          <SelectValue>{cfg.label}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value} data-testid={`status-option-${opt.value}`}>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${opt.bg} ${opt.text}`}>{opt.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  // Independent E-Way Bill Status — editable dropdown. Manual override persists via PATCH.
  const EWAY_STATUS_OPTIONS = [
    { value: "Not Required", style: { background: "#F1F2F4", color: "#5B6270", borderColor: "#E2E4E9" } },
    { value: "Pending",      style: { background: "#FFF4E0", color: "#8A5A00", borderColor: "#F1DFB5" } },
    { value: "Uploaded",     style: { background: "#E1F5EE", color: "#085041", borderColor: "#C0E4D5" } },
  ];

  const handleEwayStatusChange = async (quotationId, newStatus) => {
    const prev = quotations.find(q => q.id === quotationId)?.eway_bill_status;
    setQuotations(prevList => prevList.map(q => q.id === quotationId ? { ...q, eway_bill_status: newStatus } : q));
    try {
      await axios.patch(`${API}/quotations/${quotationId}/eway-bill-status`, { eway_bill_status: newStatus }, { withCredentials: true });
      toast.success(`E-Way Bill Status updated to ${newStatus}`);
    } catch (err) {
      setQuotations(prevList => prevList.map(q => q.id === quotationId ? { ...q, eway_bill_status: prev } : q));
      toast.error(err.response?.data?.detail || "Failed to update E-Way Bill Status");
    }
  };

  const EwayBillStatusBadge = ({ quotation }) => {
    const s = quotation.eway_bill_status || "Not Required";
    const style = (EWAY_STATUS_OPTIONS.find(o => o.value === s) || EWAY_STATUS_OPTIONS[0]).style;
    return (
      <Select value={s} onValueChange={(v) => handleEwayStatusChange(quotation.id, v)}>
        <SelectTrigger
          className="h-auto py-0.5 px-2 rounded-full text-[11px] font-medium border focus:ring-0 w-fit gap-1 whitespace-nowrap"
          style={style}
          data-testid={`eway-status-select-${quotation.id}`}
        >
          <SelectValue>{s}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {EWAY_STATUS_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value} data-testid={`eway-status-option-${opt.value}`}>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border" style={opt.style}>{opt.value}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  // Inline-editable invoice date cell. The quotation stays fully locked for every other
  // field; only invoice_date can be changed post-upload (analytics bucketing depends on it).
  const InvoiceDateCell = ({ quotation }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(quotation.invoice_date || "");
    const [saving, setSaving] = useState(false);
    const hasInvoice = !!quotation.invoice_file_url;
    
    useEffect(() => {
      setValue(quotation.invoice_date || "");
    }, [quotation.invoice_date]);
    
    if (!hasInvoice) {
      return (
        <span className="text-xs text-muted-foreground" data-testid={`invoice-date-empty-${quotation.id}`}>—</span>
      );
    }
    
    if (!isEditing) {
      return (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="group inline-flex items-center gap-1.5 text-xs hover:bg-slate-50 rounded px-1.5 py-0.5 -mx-1.5 transition-colors"
          data-testid={`invoice-date-display-${quotation.id}`}
          title="Click to edit invoice date"
        >
          <span className="font-mono text-slate-800">
            {quotation.invoice_date
              ? new Date(quotation.invoice_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
              : "Set date"}
          </span>
          <Edit className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      );
    }
    
    const handleSave = async () => {
      if (!value) {
        toast.error("Please pick a date");
        return;
      }
      setSaving(true);
      try {
        await axios.patch(
          `${API}/quotations/${quotation.id}/invoice-date`,
          { invoice_date: value },
          { withCredentials: true },
        );
        toast.success("Invoice date updated");
        setIsEditing(false);
        fetchQuotations();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to update invoice date");
      } finally {
        setSaving(false);
      }
    };
    
    return (
      <div className="flex items-center gap-1" data-testid={`invoice-date-editor-${quotation.id}`}>
        <Input
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="h-7 w-[136px] text-xs font-mono"
          disabled={saving}
          autoFocus
        />
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-emerald-600 hover:bg-emerald-50"
          onClick={handleSave}
          disabled={saving}
          data-testid={`invoice-date-save-${quotation.id}`}
          title="Save"
        >
          ✓
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-slate-500 hover:bg-slate-100"
          onClick={() => { setIsEditing(false); setValue(quotation.invoice_date || ""); }}
          disabled={saving}
          data-testid={`invoice-date-cancel-${quotation.id}`}
          title="Cancel"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const config = statusConfig(status);
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="quotations-log-title">Quotations Log</h1>
            <p className="text-muted-foreground mt-1">View and manage all quotations</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setOnlineInvoiceOpen(true)} variant="outline" data-testid="upload-online-invoice-button">
              <Receipt className="h-4 w-4 mr-2" />
              Upload Online Invoice
            </Button>
            {userRole === "admin" && (
              <Button onClick={() => setBulkUploadOpen(true)} variant="outline" data-testid="bulk-upload-quotations-button">
                <Upload className="h-4 w-4 mr-2" />
                Upload Quotations
              </Button>
            )}
            <Button onClick={() => setAdvancedExportOpen(true)} variant="outline" data-testid="advanced-export-button">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Advanced Export
            </Button>
            <Button onClick={handleExport} variant="outline" data-testid="export-quotations-button">
              <Download className="h-4 w-4 mr-2" />
              Export All
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>All Quotations ({totalCount})</CardTitle>
            {selectedIds.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" data-testid="bulk-delete-quotations-button">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedIds.length})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Selected Quotations?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete {selectedIds.length} selected quotation(s)? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </CardHeader>
          <CardContent>
            {/* Sticky Search and Filters */}
            <div className="sticky top-0 z-10 bg-white pb-4 mb-4 border-b">
              <div className="flex flex-wrap gap-3 items-center">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by Quotation ID or Seller Name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="quotations-search-input"
                  />
                </div>
                
                {/* Date Range Filter - Shopify-style */}
                <DateRangeFilter
                  from={appliedDateFrom}
                  to={appliedDateTo}
                  onChange={(f, t) => {
                    setDateFrom(f);
                    setDateTo(t);
                    setAppliedDateFrom(f);
                    setAppliedDateTo(t);
                  }}
                  testId="quotations-date-range"
                />
                
                {/* Status Filter — NEW, multi-select */}
                <MultiSelectFilter
                  label="All Statuses"
                  className="w-[170px]"
                  testId="quotations-status-filter"
                  selected={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: "draft", label: "Draft" },
                    { value: "mail_sent", label: "Mail Sent" },
                    { value: "processing", label: "Processing" },
                    { value: "ready_for_upload", label: "Ready for Upload" },
                    { value: "validation_failed", label: "Validation Failed" },
                    { value: "invoice_uploaded", label: "Invoice Uploaded" },
                  ]}
                />

                {/* Quotation Type Filter — multi-select */}
                <MultiSelectFilter
                  label="All Types"
                  className="w-[140px]"
                  testId="quotation-type-filter"
                  selected={typeFilter}
                  onChange={setTypeFilter}
                  options={[
                    { value: "online", label: "Online" },
                    { value: "offline", label: "Offline" },
                  ]}
                />
                
                {/* Seller Filter — searchable multi-select */}
                <SellerMultiSelect
                  sellers={sellers}
                  selectedIds={sellerFilter}
                  onChange={setSellerFilter}
                  placeholder="All Sellers"
                />

                {/* Validation Filter — renamed from Discount Status, multi-select */}
                <MultiSelectFilter
                  label="All Validations"
                  className="w-[170px]"
                  testId="quotations-validation-filter"
                  selected={discountStatusFilter}
                  onChange={setDiscountStatusFilter}
                  options={[
                    { value: "valid", label: "Valid" },
                    { value: "over_discount", label: "Over Discount" },
                  ]}
                />
                
                {/* Clear All Filters */}
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-all-filters-button">
                    <X className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
              
              {/* Active Date Range Display */}
              {hasActiveDateRange && (
                <div className="flex items-center gap-2 text-sm mt-3">
                  <span className="text-muted-foreground">Date Range:</span>
                  <span className="font-medium text-accent" data-testid="active-date-range-display">
                    {getDateRangeDisplayText()}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2 text-muted-foreground hover:text-destructive"
                    onClick={clearDateRangeFilter}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-auto max-h-[600px]">
                <Table containerClassName="overflow-visible">
                  <TableHeader className="sticky top-0 z-20">
                    <TableRow className="bg-slate-100 hover:bg-slate-100">
                      <TableHead className="w-12 bg-slate-100 border-b-2 border-slate-200">
                        <Checkbox
                          checked={filteredQuotations.length > 0 && selectedIds.length === filteredQuotations.length}
                          onCheckedChange={handleSelectAll}
                          data-testid="select-all-quotations-checkbox"
                        />
                      </TableHead>
                      <TableHead className="bg-slate-100 border-b-2 border-slate-200 font-semibold text-slate-700">Quotation ID</TableHead>
                      <SortableHeader sortKey="order_date" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Date</SortableHeader>
                      <SortableHeader sortKey="seller_name" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Seller Name</SortableHeader>
                      <SortableHeader sortKey="quotation_type" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Type</SortableHeader>
                      <SortableHeader sortKey="total_invoice_value" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Invoice Value</SortableHeader>
                      <SortableHeader sortKey="invoice_date" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Invoice Date</SortableHeader>
                      <SortableHeader sortKey="status" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Status</SortableHeader>
                      <SortableHeader sortKey="eway_bill_status" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">E-Way Bill Status</SortableHeader>
                      <SortableHeader sortKey="final_status" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Validation</SortableHeader>
                      <TableHead className="text-right bg-slate-100 border-b-2 border-slate-200 font-semibold text-slate-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {filteredQuotations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                        {quotations.length === 0 
                          ? "No quotations found. Create your first quotation to get started."
                          : "No quotations match your search/filter criteria."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedQuotations.map((quotation) => (
                      <TableRow 
                        key={quotation.id} 
                        className="cursor-pointer hover:bg-slate-50"
                        data-testid={`quotation-row-${quotation.id}`}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.includes(quotation.id)}
                            onCheckedChange={(checked) => handleSelectOne(quotation.id, checked)}
                            data-testid={`select-quotation-${quotation.id}`}
                          />
                        </TableCell>
                        <TableCell 
                          className="font-mono font-semibold"
                          onClick={() => navigate(`/quotations/${quotation.id}`)}
                        >
                          <div className="inline-flex items-center gap-1.5">
                            <span>{quotation.quotation_id}</span>
                            {hasInvoice(quotation) && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium font-sans"
                                style={{ background: "#F3F4F6", color: "#4B5563", border: "0.5px solid #D1D5DB" }}
                                title={userRole === "admin" ? "Invoice uploaded — admin override: still editable" : "Locked — invoice uploaded"}
                                data-testid={`locked-pill-${quotation.id}`}
                              >
                                <Lock className="h-2.5 w-2.5" />
                                {userRole === "admin" ? "Invoice" : "Locked"}
                              </span>
                            )}
                            {quotation.remarks && quotation.remarks.trim() !== "" && (
                              <span
                                className="inline-flex items-center justify-center gap-0.5 rounded-full"
                                style={{ background: "#FAEEDA", color: "#8A5A00", padding: "0 5px", height: 16 }}
                                title={`Remarks: ${quotation.remarks}${Array.isArray(quotation.remark_categories) && quotation.remark_categories.length ? `\nCategories: ${quotation.remark_categories.join(", ")}` : ""}`}
                                data-testid={`remarks-pill-${quotation.id}`}
                              >
                                <MessageSquare className="h-2.5 w-2.5" />
                                {Array.isArray(quotation.remark_categories) && quotation.remark_categories.length > 0 && (
                                  <span className="text-[9px] font-semibold leading-none">{quotation.remark_categories.length}</span>
                                )}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell onClick={() => navigate(`/quotations/${quotation.id}`)}>
                          {new Date(quotation.order_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell onClick={() => navigate(`/quotations/${quotation.id}`)}>
                          {quotation.seller_name}
                        </TableCell>
                        <TableCell onClick={() => navigate(`/quotations/${quotation.id}`)}>
                          {(() => {
                            const t = (quotation.quotation_type || "offline").toLowerCase();
                            const isOnline = t === "online";
                            return (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
                                style={isOnline
                                  ? { background: "#E6F1FB", color: "#0C447C", borderColor: "#C7DEF3" }
                                  : { background: "#F1EFE8", color: "#444441", borderColor: "#E0DDD3" }}
                                data-testid={`quotation-type-badge-${quotation.id}`}
                              >
                                {isOnline ? "Online" : "Offline"}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell 
                          className="font-mono"
                          onClick={() => navigate(`/quotations/${quotation.id}`)}
                        >
                          ₹{quotation.total_invoice_value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <InvoiceDateCell quotation={quotation} />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <StatusCell quotation={quotation} />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <EwayBillStatusBadge quotation={quotation} />
                        </TableCell>
                        <TableCell onClick={() => navigate(`/quotations/${quotation.id}`)}>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            quotation.final_status === "valid" 
                              ? "bg-success/10 text-success" 
                              : "bg-destructive/10 text-destructive"
                          }`}>
                            {quotation.final_status === "valid" ? "Valid" : "Over Discount"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/quotations/${quotation.id}`)}
                              data-testid={`view-quotation-${quotation.id}`}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={isLocked(quotation)}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/quotations/${quotation.id}/edit`);
                              }}
                              data-testid={`edit-quotation-${quotation.id}`}
                              title={isLocked(quotation) ? "Locked — invoice uploaded" : "Edit Quotation"}
                            >
                              {isLocked(quotation) ? <Lock className="h-4 w-4 text-muted-foreground" /> : <Edit className="h-4 w-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={isLocked(quotation)}
                              onClick={(e) => handleEmailClick(quotation, e)}
                              data-testid={`email-quotation-${quotation.id}`}
                              title={isLocked(quotation) ? "Disabled after invoice upload" : "Send Email"}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" onClick={(e) => e.stopPropagation()} data-testid={`more-actions-${quotation.id}`} title="More actions">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
                                {!isLocked(quotation) && (
                                  <DropdownMenuItem onClick={() => handleUploadInvoiceClick(quotation)} data-testid={`menu-upload-invoice-${quotation.id}`}>
                                    <Upload className="h-4 w-4 mr-2" />Upload Invoice
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={(e) => handleDownloadQuotation(quotation, e)} data-testid={`menu-download-excel-${quotation.id}`}>
                                  <FileSpreadsheet className="h-4 w-4 mr-2" />Download Excel
                                </DropdownMenuItem>
                                {quotation.invoice_file_url && (
                                  <DropdownMenuItem onClick={() => handleDownloadInvoice(quotation)} data-testid={`menu-download-invoice-${quotation.id}`}>
                                    <FileText className="h-4 w-4 mr-2" />Download Invoice
                                  </DropdownMenuItem>
                                )}
                                {quotation.eway_bill_file_url && (
                                  <DropdownMenuItem onClick={() => handleDownloadEwayBill(quotation)} data-testid={`menu-download-eway-${quotation.id}`}>
                                    <Receipt className="h-4 w-4 mr-2" />Download E-Way Bill
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  disabled={isLocked(quotation)}
                                  onClick={(e) => handleDeleteClick(quotation, e)}
                                  data-testid={`menu-delete-${quotation.id}`}
                                >
                                  {isLocked(quotation) ? <Lock className="h-4 w-4 mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                                  Delete {isLocked(quotation) && <span className="ml-1 text-[10px] text-muted-foreground">(locked)</span>}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
              {/* Pagination controls */}
              {totalCount > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-2 py-3 border-t border-slate-100 mt-1" data-testid="quotations-pagination">
                  <p className="text-sm text-muted-foreground" data-testid="pagination-info">
                    Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || loading}
                      data-testid="pagination-prev"
                    >
                      Previous
                    </Button>
                    <span className="text-sm px-2" data-testid="pagination-current">Page {page} of {Math.max(1, totalPages)}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                      disabled={page >= totalPages || loading}
                      data-testid="pagination-next"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {selectedQuotation && (
        <EmailModal
          quotation={selectedQuotation}
          seller={selectedSeller}
          open={emailModalOpen}
          onClose={() => {
            setEmailModalOpen(false);
            setSelectedQuotation(null);
            setSelectedSeller(null);
          }}
          onSuccess={fetchQuotations}
        />
      )}

      <AdvancedExportModal
        open={advancedExportOpen}
        onClose={() => setAdvancedExportOpen(false)}
      />

      {invoiceUploadQuotation && (
        <InvoiceUploadModal
          quotation={invoiceUploadQuotation}
          open={!!invoiceUploadQuotation}
          onClose={() => setInvoiceUploadQuotation(null)}
          onSuccess={fetchQuotations}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this quotation?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete quotation {quotationToDelete?.quotation_id} from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm} 
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Upload Quotations Modal */}
      <BulkUploadQuotationsModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onSuccess={fetchQuotations}
      />

      {/* Online Invoice Upload Modal */}
      <OnlineInvoiceUploadModal
        open={onlineInvoiceOpen}
        onClose={() => setOnlineInvoiceOpen(false)}
        onSuccess={fetchQuotations}
      />
    </>
  );
}